/**
 * M-H1 — Hive Skill Add modal.
 *
 * Sprint 133 Stream C. Lets a tenant admin attach a published skill to a
 * hive. The binding inherits to all member agents (priority + mode are
 * configurable; agent-level bindings always win on conflict).
 *
 * Mirrors the agent-level add modal flow: search the catalog, pick a
 * skill, choose mode/priority, submit.
 */

import { useEffect, useMemo, useState } from 'react';
import { DiscardChangesDialog } from '@vanguard-atlas/shared';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui';
import { Sparkles } from 'lucide-react';
import {
  useTenantSkills,
  useUpsertHiveSkillBinding,
  useAdminSkillFacets,
} from '@/features/skills';
import { UnifiedSkillSearchInput } from '@/features/skills/components/UnifiedSkillSearchInput';
import { parseUnifiedSkillSearch } from '@/features/skills/utils/unifiedSearch';
import type {
  ActivationMode,
  SkillSummary,
} from '@/features/skills';
import { useI18n } from '@/i18n';
import { getErrorMessage } from '@/lib/api-client';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  hiveId: string;
  /** Skill IDs already bound to this hive (filtered out of the picker). */
  alreadyBoundSkillIds: Set<string>;
}

export function HiveSkillAddModal({
  open,
  onOpenChange,
  hiveId,
  alreadyBoundSkillIds,
}: Props) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<SkillSummary | null>(null);
  const [mode, setMode] = useState<ActivationMode>('auto');
  const [priority, setPriority] = useState<number>(50);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const isDirty = !!selected || mode !== 'auto' || priority !== 50;
  const discardLabels = {
    title: 'Discard changes?',
    message: 'You have unsaved changes. If you close now, they will be lost.',
    confirm: 'Discard',
    cancel: 'Keep editing',
  };
  const handleClose = () => {
    if (isDirty) {
      setConfirmDiscardOpen(true);
      return;
    }
    onOpenChange(false);
  };

  const { t } = useI18n();

  const MODE_OPTIONS: { value: ActivationMode; label: string; hint: string }[] = [
    { value: 'auto', label: 'auto', hint: t('admin.agents.hive.skillAdd.hintAuto') },
    { value: 'always', label: 'always', hint: t('admin.agents.hive.skillAdd.hintAlwaysOn') },
    { value: 'manual', label: 'manual', hint: t('admin.agents.hive.skillAdd.hintManual') },
    { value: 'triggered', label: 'triggered', hint: t('admin.agents.hive.skillAdd.hintAlwaysOff') },
  ];

  const { data: facets } = useAdminSkillFacets();
  const parsed = useMemo(() => parseUnifiedSkillSearch(query), [query]);
  const { data: list, isLoading } = useTenantSkills({
    search: parsed.search,
    vertical: parsed.vertical,
    function: parsed.functionName,
    tags_all: parsed.tagsAll.length ? parsed.tagsAll : undefined,
    tags_none: parsed.tagsNone.length ? parsed.tagsNone : undefined,
    lifecycle: 'published',
    page_size: 50,
  });
  const upsert = useUpsertHiveSkillBinding(hiveId);

  // Reset state on open.
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelected(null);
      setMode('auto');
      setPriority(50);
    }
  }, [open]);

  const candidates = useMemo<SkillSummary[]>(() => {
    const items = list?.data ?? [];
    return items.filter((s) => !alreadyBoundSkillIds.has(s.id));
  }, [list, alreadyBoundSkillIds]);

  async function handleSubmit() {
    if (!selected) return;
    try {
      await upsert.mutateAsync({
        skill_id: selected.id,
        enabled: true,
        priority,
        mode,
      });
      toast.success(`${selected.display_name ?? selected.slug} attached to hive`);
      onOpenChange(false);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) { handleClose(); } }}>
      <DialogContent
        className="max-w-2xl"
        dirty={isDirty}
        discardTitle={discardLabels.title}
        discardMessage={discardLabels.message}
        discardConfirmLabel={discardLabels.confirm}
        discardCancelLabel={discardLabels.cancel}
      >
        <DialogHeader>
          <DialogTitle>{t('admin.agents.hive.skillAdd.title')}</DialogTitle>
          <DialogDescription>
            {t('admin.agents.hive.skillAdd.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1 — pick a skill */}
          <div className="space-y-2">
            <Label>{t('admin.agents.hive.skillAdd.searchLabel')}</Label>
            <UnifiedSkillSearchInput
              autoFocus
              value={query}
              onChange={setQuery}
              facets={{
                verticals: facets?.verticals ?? [],
                functions: facets?.functions ?? [],
                tags: facets?.tags ?? [],
              }}
              placeholder={t('admin.agents.hive.skillAdd.searchPlaceholder')}
            />
          </div>

          <div className="border rounded-md max-h-64 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-6">
                <Spinner />
              </div>
            ) : candidates.length === 0 ? (
              <div className="text-center py-6 text-sm text-muted-foreground">
                {t('admin.agents.hive.skillAdd.noSkills')}
              </div>
            ) : (
              <ul className="divide-y">
                {candidates.map((s) => (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => setSelected(s)}
                      className={`w-full flex items-center gap-3 p-3 text-left hover:bg-muted/50 transition-colors ${
                        selected?.id === s.id ? 'bg-primary/5 ring-1 ring-primary/30' : ''
                      }`}
                    >
                      <Sparkles className="h-4 w-4 text-primary flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">
                          {s.display_name ?? s.slug}
                        </div>
                        <div className="text-xs text-muted-foreground font-mono truncate">
                          {s.slug} · {s.vertical}/{s.function}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Step 2 — binding parameters */}
          {selected && (
            <div className="grid grid-cols-2 gap-4 pt-2 border-t">
              <div className="space-y-1">
                <Label>{t('admin.agents.hive.skillAdd.activationMode')}</Label>
                <select
                  value={mode}
                  onChange={(e) => setMode(e.target.value as ActivationMode)}
                  className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                >
                  {MODE_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label} — {m.hint}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label>{t('admin.agents.hive.skillAdd.priority')}</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={priority}
                  onChange={(e) =>
                    setPriority(Math.max(0, Math.min(100, Number(e.target.value) || 0)))
                  }
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!selected || upsert.isPending}
            onClick={handleSubmit}
          >
            {upsert.isPending ? t('admin.agents.hive.skillAdd.attaching') : t('admin.agents.hive.skillAdd.attach')}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* ADR-114 enmienda 1: confirmación de descarte */}
      <DiscardChangesDialog
        open={confirmDiscardOpen}
        onDiscard={() => {
          setConfirmDiscardOpen(false);
          onOpenChange(false);
        }}
        onCancel={() => setConfirmDiscardOpen(false)}
        title={discardLabels.title}
        message={discardLabels.message}
        confirmLabel={discardLabels.confirm}
        cancelLabel={discardLabels.cancel}
      />
    </Dialog>
  );
}

export default HiveSkillAddModal;
